import { Button, Form, Input } from "@oxygen/core";
import { makeCancelable } from "GlobalUtils/CancelablePromise";
import React, { memo, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { logIn, setCurrentScreen } from "../actionCreator";
import CarwaleLoginApi from "../apis";
import { EmailInput, PasswordInput } from "../components/FormInputs";
import { SCREEN } from "../constants/Global";
import { getValidationStatus, validateInput } from "../utils";
import SpeedometerLoader from "GlobalComponent/SpeedometerLoader";
import PropTypes from "prop-types";
import useInput from "../hooks/useInput";
import useReCaptcha from "../hooks/useReCaptcha";
import {
  validateEmail,
  validateName,
  validateMobile,
  validatePassword,
} from "GlobalUtils/Validate";
import ReCAPTCHA from "react-google-recaptcha";
import { RECAPTCHA_SITE_KEY } from "GlobalConstant/";
import getThemeClasses from "@oxygen/theme";

const propTypes = {
  /** Used to show toast on user actions */
  showToast: PropTypes.func.isRequired,
};

const loginContainerClasses = getThemeClasses(`
  font-size-7.5;
  text-align-center;
  mt-5;
`);
const loginTextClasses = getThemeClasses(`
  color-blue-light;
  ml-1;
  cursor-pointer;
`);

/**
 * This component renders SignUp content
 * Handles api calls and validations
 */
let apiPromise = null;

const SignUp = memo(function SignUp({ platForm, showToast }) {
  const dispatch = useDispatch();
  const [name, setName, onNameChange] = useInput(validateName);
  const [email, setEmail, onEmailChange] = useInput(validateEmail);
  const [mobile, setMobile, onMobileChange] = useInput(validateMobile);
  const [password, setPassword, onPasswordChange] = useInput(validatePassword);
  const [isFetching, setIsFetching] = useState(false);
  const { reCaptchaRef, triggerRecaptcha } = useReCaptcha();

  useEffect(() => {
    return () => {
      apiPromise && apiPromise.cancel();
    };
  }, []);

  function handleSignupApiResponse(response) {
    if (response && response.status === 200) {
      if (!response.isRegistered) {
        showToast(response.message);
      } else {
        dispatch(
          logIn({
            customerId: response.customerId,
            name: name.value,
            email: email.value,
            mobile: mobile.value,
          })
        );
        showToast("Your account has been created successfully");
      }
      return;
    }
    showToast((response && response.message) || "Something went wrong");
    setIsFetching(false);
  }
  function validateBeforeSubmit() {
    const inputValidation = validateInput({
      name: name.value,
      email: email.value,
      password: password.value,
      mobile: mobile.value,
    });
    const { isValid } = inputValidation;
    if (!isValid) {
      const { validation } = inputValidation;
      setName({ ...name, ...validation.name });
      setEmail({ ...email, ...validation.email });
      setPassword({ ...password, ...validation.password });
      setMobile({ ...mobile, ...validation.mobile });
    }
    return isValid;
  }
  function onSubmit(event) {
    event.preventDefault();
    const isUserInputsValid = validateBeforeSubmit();
    if (!isUserInputsValid) {
      return;
    }
    triggerRecaptcha();
  }
  function handleSignUp(reCaptchaResponse) {
    const userName = name.value.trim().replace(/\s+/, " ");
    setIsFetching(true);
    apiPromise && apiPromise.cancel();
    apiPromise = makeCancelable(
      CarwaleLoginApi.cwSignup({
        name: userName,
        email: email.value,
        password: password.value,
        mobile: mobile.value,
        reCaptchaResponse,
        platForm,
      })
    );
    apiPromise.promise.then(handleSignupApiResponse).catch(error => {
      console.log("Signup Api failed", error);
      showToast("SignUp failed");
      setIsFetching(false);
    });
  }

  function handleLoginClick() {
    dispatch(setCurrentScreen(SCREEN.LOG_IN));
  }

  return (
    <>
      {isFetching && <SpeedometerLoader />}
      <Form>
        <Form.Item
          validateStatus={getValidationStatus(name.isValid)}
          helperText={name.errMessage}
        >
          <Input
            id="name"
            label="name"
            placeholder="Enter Your Full Name"
            value={name.value}
            onChange={onNameChange}
          />
        </Form.Item>
        <EmailInput
          email={email}
          label="email"
          handleEmailInput={onEmailChange}
        />
        <Form.Item
          validateStatus={getValidationStatus(mobile.isValid)}
          helperText={mobile.errMessage}
        >
          <Input
            id="mobile"
            label="mobile"
            placeholder="Enter Mobile Number"
            prefix="+91"
            maxLength="10"
            type="tel"
            value={mobile.value}
            onChange={onMobileChange}
          />
        </Form.Item>
        <PasswordInput
          password={password}
          label="password"
          handlePasswordInput={onPasswordChange}
        />
        <Button htmlType="submit" block onClick={onSubmit}>
          Sign Up
        </Button>
      </Form>
      <div className={loginContainerClasses}>
        <span>Already have an account?</span>
        <span className={loginTextClasses} onClick={handleLoginClick}>
          Login
        </span>
      </div>
      <ReCAPTCHA
        ref={reCaptchaRef}
        sitekey={RECAPTCHA_SITE_KEY}
        size="invisible"
        badge="bottomright"
        onChange={handleSignUp}
      />
    </>
  );
});

SignUp.displayName = "SignUp";
SignUp.propTypes = propTypes;
export default SignUp;
